import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, LogOut, Shield, Bell, Edit2, Camera, UserPlus, UserX } from 'lucide-react';
import { getUserInfo, isOAuthUser, getPlatform, withdrawUser } from '../../services/api';
import { useAlert } from '../Common/Alert';
import AlertModal from '../Common/Alert';
import ContactFooter from '../Common/ContactFooter';
import './ProfilePage.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { alert, showAlert, closeAlert, showAuthAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [currentUsername, setCurrentUsername] = useState('');

  const fetchUserInfo = async () => {
    try {
      const userInfo = await getUserInfo();
      setCurrentUsername(userInfo.userNickname);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      setCurrentUsername('익명사용자');
    }
  };
  
  useEffect(() => {
    // 프로필 데이터 로드
    setLoading(true);
    fetchUserInfo();
    setLoading(false);
  }, []);

  // 쿠키 삭제 헬퍼 함수
  const deleteCookie = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  const handleLogout = () => {
    showAlert('logout', '로그아웃 하시겠습니까?', {
      confirmLabel: '로그아웃',
      cancelable: true,
      autoClose: false,
      onConfirm: () => {
        // iOS/Android 앱에 refreshToken 삭제 요청
        if (getPlatform() === 'ios') {
          if (window.webkit?.messageHandlers?.deleteRefreshToken) {
            window.webkit.messageHandlers.deleteRefreshToken.postMessage(null);
          }
        } else if (getPlatform() === 'android') {
          if (window.deleteRefreshToken) {
            window.deleteRefreshToken.postMessage('');
          }
        }

        // 쿠키 + localStorage 정리
        deleteCookie('accessToken');
        deleteCookie('refreshToken');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');

        navigate('/login');
      },
    });
  };

  const handleWithdraw = () => {
    showAlert('logout', '정말 탈퇴하시겠습니까?\n탈퇴 후 모든 데이터가 삭제됩니다.', {
      confirmLabel: '탈퇴하기',
      cancelable: true,
      autoClose: false,
      onConfirm: async () => {
        try {
          await withdrawUser();

          // iOS/Android 앱에 refreshToken 삭제 요청
          if (getPlatform() === 'ios') {
            if (window.webkit?.messageHandlers?.deleteRefreshToken) {
              window.webkit.messageHandlers.deleteRefreshToken.postMessage(null);
            }
          } else if (getPlatform() === 'android') {
            if (window.deleteRefreshToken) {
              window.deleteRefreshToken.postMessage('');
            }
          }

          // 쿠키 + localStorage 정리
          deleteCookie('accessToken');
          deleteCookie('refreshToken');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');

          navigate('/login');
        } catch (error) {
          showAlert('error', error.message || '회원 탈퇴에 실패했습니다.', { autoClose: false });
        }
      },
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-loading">
          <div className="loading-spinner"></div>
          <p>프로필 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <AlertModal alert={alert} onClose={closeAlert} />
    <div className="profile-container">
      {/* 헤더 */}
      <header className="profile-header">
        <div className="header-content">
          <div className="header-row">
            <button className="back-button" onClick={handleBack}>
              <ArrowLeft size={24} />
            </button>
            <div className="header-spacer"></div>
          </div>
        </div>
      </header>

      {/* 프로필 컨텐츠 */}
      <div className="profile-content">
        {/* 프로필 카드 */}
        <div className="profile-card">
          {/* 프로필 이미지 섹션 */}
          {/* <div className="profile-image-section"> */}
            {/* <div className="profile-image-wrapper">
              {userData?.profileImage || userData?.profile_image ? (
                <img
                  src={userData?.profileImage || userData?.profile_image}
                  alt="프로필"
                  className="profile-image"
                />
              ) : (
                <div className="profile-image-placeholder">
                  <User size={48} className="text-gray-400" />
                </div>
              )}
              <button className="profile-image-edit">
                <Camera size={16} />
              </button>
            </div>
            <div className="profile-name-section">
              <h2 className="profile-name">{userData?.nickname || userData?.name || '사용자'}</h2>
              <p className="profile-email">{userData?.email || '이메일 없음'}</p>
            </div>
          </div> */}

          {/* 프로필 정보 */}
          <div className="profile-info-section">
            <div className="info-item">
              <div className="info-icon">
                <User size={20} className="text-indigo-600" />
              </div>
              <div className="info-content">
                <div className="info-label">닉네임</div>
                <div className="info-value">{currentUsername}</div>
              </div>
              {/* <button className="info-edit">
                <Edit2 size={16} />
              </button> */}
            </div>
          </div>
        </div>

        {isOAuthUser() ? (
          <>
            <div className="settings-section">
              <h3 className="settings-title">설정</h3>

              <div className="settings-card">
                <button className="settings-item" onClick={() => showAlert('info', '알림 설정 기능을 준비 중이에요.\n업데이트를 통해 곧 만나보실 수 있습니다.', { autoClose: true })}>
                  <div className="settings-icon">
                    <Bell size={20} className="text-yellow-600" />
                  </div>
                  <div className="settings-content">
                    <div className="settings-label">알림 설정</div>
                    <div className="settings-desc">푸시 알림 및 이메일 설정</div>
                  </div>
                  <div className="settings-arrow">›</div>
                </button>
              </div>
            </div>

            <div className="account-section">
              <h3 className="settings-title">계정</h3>

              <div className="settings-card">
                <button className="settings-item logout-item" onClick={handleLogout}>
                  <div className="settings-icon">
                    <LogOut size={20} className="text-red-600" />
                  </div>
                  <div className="settings-content">
                    <div className="settings-label text-red-600">로그아웃</div>
                  </div>
                </button>
                <button className="settings-item logout-item" onClick={handleWithdraw}>
                  <div className="settings-icon">
                    <UserX size={20} className="text-red-600" />
                  </div>
                  <div className="settings-content">
                    <div className="settings-label text-red-600">회원 탈퇴</div>
                  </div>
                </button>
              </div>
            </div>
          </>
        ) : 
            <div className="account-section">
              <h3 className="settings-title">계정</h3>

              <div className="settings-card">
                <button className="settings-item" onClick={() => {
                  const platform = getPlatform();
                  if (platform === 'web') {
                    showAuthAlert();
                  } else {
                    navigate('/login');
                  }
                }}>
                  <div className="settings-icon">
                    <UserPlus size={20} className="text-indigo-600" />
                  </div>
                  <div className="settings-content">
                    <div className="settings-label text-indigo-600">가입하기</div>
                  </div>
                </button>
              </div>
            </div>
          }
      </div>

      <ContactFooter />
    </div>
    </>
  );
};

export default ProfilePage;
